import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button, Card } from '@wundo/ui';
import { PILOT_CONTEXT_KEY, fetchPilotContext } from '@wundo/client/pilot.ts';
import { m } from '@/paraglide/messages.js';

/**
 * What a pilot member's weeks look like, said before anything is asked of them.
 *
 * The same screen as "wat je krijgt" in every way but its three items: somebody
 * in a programme is not weighing Wundo up, they are being told what the weeks
 * ahead hold. Nothing is stored here — it reads, and it moves on.
 */
export const Route = createFileRoute('/onboarding/how-it-works')({
  component: OnboardingHowItWorksPage,
});

interface Step {
  title: () => string;
  description: () => string;
}

const STEPS: Step[] = [
  {
    title: m.onboarding_pilot_how_practice_title,
    description: m.onboarding_pilot_how_practice_desc,
  },
  {
    title: m.onboarding_pilot_how_feedback_title,
    description: m.onboarding_pilot_how_feedback_desc,
  },
  {
    title: m.onboarding_pilot_how_real_title,
    description: m.onboarding_pilot_how_real_desc,
  },
];

export function OnboardingHowItWorksPage() {
  const navigate = useNavigate();

  const { data: pilot } = useQuery({
    queryKey: PILOT_CONTEXT_KEY,
    queryFn: fetchPilotContext,
  });

  return (
    <Card
      padding='none'
      className='mx-auto w-full max-w-150 px-11 pt-11 pb-9 max-mobile:px-6 max-mobile:pt-8'
    >
      <p className='mb-3.5 text-xs font-bold tracking-wider text-ink-muted uppercase'>
        {m.onboarding_pilot_how_kicker()}
      </p>
      <h1 className='mb-6 text-xl font-medium tracking-tight'>
        {m.onboarding_pilot_how_title({ weeks: pilot?.weeks ?? 0 })}
      </h1>

      <ul className='flex flex-col gap-5'>
        {STEPS.map((step) => (
          <li key={step.title()} className='flex gap-3.5'>
            {/* A dot, not a tick: nothing here is done yet, it is what is coming. */}
            <span
              aria-hidden='true'
              className='mt-2 size-2 shrink-0 rounded-full bg-accent'
            />
            <div className='min-w-0'>
              <p className='text-base font-bold text-ink'>{step.title()}</p>
              <p className='text-sm text-ink-muted'>{step.description()}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className='mt-8 flex items-center gap-3'>
        <Button
          variant='accent'
          shape='block'
          size='lg'
          onClick={() => void navigate({ to: '/onboarding/ready-to-practise' })}
        >
          {m.onboarding_pilot_how_continue()}
          <ArrowRight aria-hidden='true' className='size-4' />
        </Button>
        <Button
          variant='ghost'
          shape='block'
          size='lg'
          onClick={() => void navigate({ to: '/onboarding/welcome' })}
        >
          {m.onboarding_back()}
        </Button>
      </div>

      {/* The one thing people ask before they say anything out loud. */}
      <p className='mt-7 text-sm text-ink-muted'>{m.onboarding_pilot_how_footnote()}</p>
    </Card>
  );
}
